import React, { useCallback, useEffect, useState, useRef } from 'react';
import { X, AlertCircle, Clock, MapPin, Users, User, Navigation, Euro, AlertTriangle, CheckCircle, GripVertical } from 'lucide-react';
import { EmergencyIntervention } from './EmergencyHoverTooltip';
interface EmergencyDetailModalProps {
  intervention: EmergencyIntervention;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (quote: {
    operatorId: number;
    position: string;
    price: number;
  }) => void;
  position: {
    x: number;
    y: number;
  } | null;
}
const TEAM_MEMBERS = [{
  id: 1,
  name: 'Lucia Martini',
  role: 'Operatore sul Campo'
}, {
  id: 2,
  name: 'Marco Rossi',
  role: 'Operatore Senior'
}, {
  id: 4,
  name: 'Alessandro Verdi',
  role: 'Operatore sul Campo'
}, {
  id: 6,
  name: 'Roberto Gallo',
  role: 'Operatore sul Campo'
}];
const PANEL_WIDTH = 380;
const PANEL_MAX_HEIGHT = 560;
const PADDING = 16;
function clampPosition(x: number, y: number): {
  x: number;
  y: number;
} {
  const maxX = window.innerWidth - PANEL_WIDTH - PADDING;
  const maxY = window.innerHeight - PANEL_MAX_HEIGHT - PADDING;
  return {
    x: Math.max(PADDING, Math.min(x, maxX)),
    y: Math.max(PADDING, Math.min(y, maxY))
  };
}
export function EmergencyDetailModal({
  intervention,
  isOpen,
  onClose,
  onSubmit,
  position
}: EmergencyDetailModalProps) {
  const [selectedOperator, setSelectedOperator] = useState<number | ''>('');
  const [positionType, setPositionType] = useState<'hq' | 'custom'>('hq');
  const [customPosition, setCustomPosition] = useState('');
  const [price, setPrice] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  // Drag state
  const [panelPos, setPanelPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    panelX: number;
    panelY: number;
  } | null>(null);
  // Set initial position when opening (once)
  useEffect(() => {
    if (isOpen && position && !panelPos) {
      const OFFSET = 24;
      const MARKER_SIZE = 34;
      let left = position.x + MARKER_SIZE / 2 + OFFSET;
      let top = position.y - 80;
      // If too far right, show to the left
      if (left + PANEL_WIDTH > window.innerWidth - PADDING) {
        left = position.x - MARKER_SIZE / 2 - PANEL_WIDTH - OFFSET;
      }
      const clamped = clampPosition(left, top);
      setPanelPos(clamped);
    }
  }, [isOpen, position]);
  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setPanelPos(null);
    }
  }, [isOpen]);
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedOperator('');
      setPositionType('hq');
      setCustomPosition('');
      setPrice('');
      setIsSubmitting(false);
    }
  }, [isOpen, intervention.id]);
  // Timer logic
  useEffect(() => {
    if (!isOpen) return;
    const updateTimer = () => {
      const now = Date.now();
      const diff = intervention.deadlineMs - now;
      if (diff <= 0) {
        setTimeLeft('Scaduto');
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(diff % (1000 * 60) / 1000);
      setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [isOpen, intervention.deadlineMs]);
  // Drag handlers
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (!panelPos) return;
    isDraggingRef.current = true;
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      panelX: panelPos.x,
      panelY: panelPos.y
    };
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }, [panelPos]);
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !dragStartRef.current) return;
      const dx = e.clientX - dragStartRef.current.mouseX;
      const dy = e.clientY - dragStartRef.current.mouseY;
      const newPos = clampPosition(dragStartRef.current.panelX + dx, dragStartRef.current.panelY + dy);
      setPanelPos(newPos);
    };
    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        dragStartRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOperator || !price) return;
    setIsSubmitting(true);
    setTimeout(() => {
      onSubmit({
        operatorId: Number(selectedOperator),
        position: positionType === 'hq' ? 'In Sede' : customPosition,
        price: Number(price)
      });
      setIsSubmitting(false);
    }, 1000);
  };
  if (!isOpen || !panelPos) return null;
  return <div ref={panelRef} className="fixed z-[1000] flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden" style={{
    left: `${panelPos.x}px`,
    top: `${panelPos.y}px`,
    width: `${PANEL_WIDTH}px`,
    maxHeight: `${PANEL_MAX_HEIGHT}px`
  }} onClick={e => e.stopPropagation()} data-id="element-1255">
      {/* Draggable Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-red-50/50 cursor-grab active:cursor-grabbing select-none" onMouseDown={handleDragStart} data-id="element-1256">
        <div className="flex items-center gap-2.5" data-id="element-1257">
          <GripVertical size={14} className="text-gray-400 flex-shrink-0" data-id="element-1258" />
          <div className="bg-red-100 p-1.5 rounded-lg" data-id="element-1259">
            <AlertCircle className="w-4 h-4 text-red-600" data-id="element-1260" />
          </div>
          <div className="min-w-0" data-id="element-1261">
            <h2 className="text-sm font-bold text-navy leading-tight truncate" data-id="element-1262">
              {intervention.cleaningType}
            </h2>
            <p className="text-[11px] text-red-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-1263">
              <Clock size={10} data-id="element-1264" />
              Chiusura: {timeLeft}
            </p>
          </div>
        </div>
        <button onClick={e => {
        e.stopPropagation();
        onClose();
      }} onMouseDown={e => e.stopPropagation()} className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-navy transition-colors flex-shrink-0" data-id="element-1265">
          <X size={18} data-id="element-1266" />
        </button>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-1267">
        {/* Property Details */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2 border border-gray-100" data-id="element-1268">
          <div className="flex items-start gap-2.5" data-id="element-1269">
            <MapPin size={14} className="text-gray-400 mt-0.5" data-id="element-1270" />
            <div data-id="element-1271">
              <p className="text-sm font-medium text-navy" data-id="element-1272">
                {intervention.address}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1273">{intervention.zoneName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-600 pl-6" data-id="element-1274">
            <span data-id="element-1275">{intervention.mq} mq</span>
            <span className="w-0.5 h-0.5 bg-gray-400 rounded-full" data-id="element-1276" />
            <span data-id="element-1277">{intervention.rooms} stanze</span>
            <span className="w-0.5 h-0.5 bg-gray-400 rounded-full" data-id="element-1278" />
            <span data-id="element-1279">Piano {intervention.floor}</span>
          </div>
          <div className="flex flex-wrap gap-1.5 pl-6 pt-1" data-id="element-1280">
            {intervention.tags.map(tag => <span key={tag} className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-medium text-gray-600" data-id="element-1281">
                {tag}
              </span>)}
          </div>
        </div>

        {/* Host Request */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2 border border-gray-100" data-id="element-1282">
          <div className="flex items-start gap-2.5" data-id="element-1283">
            <User size={14} className="text-gray-400 mt-0.5" data-id="element-1284" />
            <div data-id="element-1285">
              <p className="text-sm font-medium text-navy" data-id="element-1286">
                {intervention.hostName}
              </p>
              <p className="text-[10px] text-gray-500" data-id="element-1287">Superhost</p>
            </div>
          </div>
          <div className="pl-6" data-id="element-1288">
            <p className="text-xs text-gray-600 italic leading-relaxed" data-id="element-1289">
              &ldquo;{intervention.description}&rdquo;
            </p>
          </div>
          <div className="pl-6 pt-1 flex items-center gap-1.5 text-[10px] font-medium text-amber-600 bg-amber-50 w-fit px-1.5 py-0.5 rounded border border-amber-100" data-id="element-1290">
            <Clock size={10} data-id="element-1291" />
            Check-in: {intervention.nextCheckIn}
          </div>
        </div>

        {/* Competition Banner */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-center justify-between" data-id="element-1292">
          <div className="flex items-center gap-2.5" data-id="element-1293">
            <div className="bg-blue-100 p-1.5 rounded-full" data-id="element-1294">
              <Users size={14} className="text-blue-600" data-id="element-1295" />
            </div>
            <div data-id="element-1296">
              <p className="text-xs font-bold text-navy" data-id="element-1297">Competizione Attiva</p>
              <p className="text-[10px] text-blue-700" data-id="element-1298">
                {intervention.quotesReceived} preventivi inviati
              </p>
            </div>
          </div>
          <div className="text-right" data-id="element-1299">
            <p className="text-[10px] text-gray-500" data-id="element-1300">Range suggerito</p>
            <p className="text-sm font-bold text-navy" data-id="element-1301">
              €{intervention.suggestedPriceMin} - €
              {intervention.suggestedPriceMax}
            </p>
          </div>
        </div>

        {/* Quote Form */}
        <div className="border-t border-gray-100 pt-3" data-id="element-1302">
          <h3 className="text-xs font-bold text-navy uppercase tracking-wider mb-3" data-id="element-1303">
            Il Tuo Preventivo
          </h3>

          <form id="quote-form" onSubmit={handleSubmit} className="space-y-3" data-id="element-1304">
            {/* Operator Selection */}
            <div data-id="element-1305">
              <label className="block text-[10px] font-medium text-gray-700 mb-1" data-id="element-1306">
                Seleziona Operatore
              </label>
              <div className="relative" data-id="element-1307">
                <select value={selectedOperator} onChange={e => setSelectedOperator(e.target.value)} className="w-full pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none appearance-none" required data-id="element-1308">
                  <option value="" disabled data-id="element-1309">
                    Scegli dal team...
                  </option>
                  {TEAM_MEMBERS.map(member => <option key={member.id} value={member.id} data-id="element-1310">
                      {member.name}
                    </option>)}
                </select>
                <User className="absolute left-2.5 top-2 text-gray-400 w-3.5 h-3.5" data-id="element-1311" />
              </div>
            </div>

            {/* Position Selection */}
            <div data-id="element-1312">
              <label className="block text-[10px] font-medium text-gray-700 mb-1" data-id="element-1313">
                Posizione Attuale
              </label>
              <div className="flex gap-2" data-id="element-1314">
                <button type="button" onClick={() => setPositionType('hq')} className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium border transition-colors ${positionType === 'hq' ? 'bg-teal-50 border-teal-deep text-teal-deep' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1315">
                  In Sede
                </button>
                <button type="button" onClick={() => setPositionType('custom')} className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium border transition-colors ${positionType === 'custom' ? 'bg-teal-50 border-teal-deep text-teal-deep' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1316">
                  Altrove
                </button>
              </div>
            </div>

            {/* Custom Position Input */}
            {positionType === 'custom' && <div data-id="element-1317">
                <div className="relative" data-id="element-1318">
                  <input type="text" value={customPosition} onChange={e => setCustomPosition(e.target.value)} placeholder="Es. Via Appia Nuova 123" className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-teal-deep outline-none" required={positionType === 'custom'} data-id="element-1319" />
                  <Navigation className="absolute left-2.5 top-2 text-gray-400 w-3.5 h-3.5" data-id="element-1320" />
                </div>
              </div>}

            {/* Estimated Arrival */}
            {selectedOperator && <div className="flex items-center gap-2 text-[10px] text-teal-700 bg-teal-50 px-2.5 py-1.5 rounded-lg border border-teal-100" data-id="element-1321">
                <CheckCircle size={12} data-id="element-1322" />
                <span data-id="element-1323">
                  Arrivo stimato entro le <strong data-id="element-1324">14:30</strong>
                </span>
              </div>}

            {/* Price Input */}
            <div data-id="element-1325">
              <label className="block text-[10px] font-medium text-gray-700 mb-1" data-id="element-1326">
                La tua offerta (€)
              </label>
              <div className="relative" data-id="element-1327">
                <input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0.00" min={1} className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep outline-none font-bold text-navy" required data-id="element-1328" />
                <Euro className="absolute left-2.5 top-2.5 text-gray-400 w-3.5 h-3.5" data-id="element-1329" />
              </div>
              <p className="text-[10px] text-gray-500 mt-1" data-id="element-1330">
                Include commissioni CleanAI.
              </p>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 p-2.5 rounded-lg" data-id="element-1331">
              <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" data-id="element-1332" />
              <p className="text-[10px] text-amber-800 leading-relaxed" data-id="element-1333">
                <strong data-id="element-1334">Attenzione:</strong> Ritardi &gt; 30 min comportano
                penalità sul rating.
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2" data-id="element-1335">
        <button type="button" onClick={onClose} className="px-3 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-white transition-colors" data-id="element-1336">
          Annulla
        </button>
        <button type="submit" form="quote-form" disabled={isSubmitting || !selectedOperator || !price} className="px-4 py-2 bg-teal-deep text-white rounded-lg text-xs font-medium hover:bg-teal-deep/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-1337">
          {isSubmitting ? <>
              <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1338" />
              Invio...
            </> : 'Invia Preventivo'}
        </button>
      </div>
    </div>;
}